import type * as React from 'react'
import { createContext, useContext, useMemo } from 'react'
import type { JSX } from 'react'

import type { Temporal } from '@js-temporal/polyfill'

import { useCurrentTimeOverride } from '~publish/hooks/useCurrentTimeOverride'

import { useTemplateLibraryData } from '../hooks/useTemplateLibraryData'
import type { Template, TemplateCampaign } from '../types'

interface TemplateLibraryContextValue {
  /**
   * Whether template data is still loading.
   */
  loading: boolean

  /**
   * Current time used for all date-based calculations.
   * Either the preview time or Temporal.Now.
   */
  currentTime: Temporal.ZonedDateTime

  /**
   * Whether a preview date is currently set (vs using actual current time).
   */
  isPreviewSet: boolean

  /**
   * Set a preview date, or undefined to clear and use actual current time.
   */
  setPreviewDate: (date?: Temporal.ZonedDateTime) => void

  /**
   * Adjust the preview date by a number of days (positive or negative).
   */
  adjustPreviewByDays: (days: number) => void

  /**
   * Campaigns that are currently active (status='Active' and within date range).
   * Sorted by priority: Drip Campaign > most recent startDate.
   */
  activeCampaigns: TemplateCampaign[]

  /**
   * The primary (highest priority) active campaign, or undefined if none.
   */
  primaryCampaign: TemplateCampaign | undefined

  /**
   * All loaded templates, unfiltered. Use this when you need to resolve a
   * template by id regardless of time/campaign filtering (e.g. deep links).
   */
  templates: Template[]

  /**
   * Templates filtered by all time-based rules:
   * - Not before start date
   * - Not after end date
   * - Campaign is active
   * - Drip campaign day has arrived
   */
  visibleTemplates: Template[]

  /**
   * Check if a template is visible in a specific campaign based on day.
   */
  isTemplateVisibleInCampaign: (
    template: Template,
    campaign: TemplateCampaign,
  ) => boolean

  /**
   * Get campaign tags for a template (campaign names + day numbers for drip campaigns).
   */
  getTemplateCampaignTags: (template: Template) => string[]
}

const TemplateLibraryContext = createContext<
  TemplateLibraryContextValue | undefined
>(undefined)

export const TemplateLibraryProvider = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const { currentTime, isPreviewSet, setPreviewDate, adjustPreviewByDays } =
    useCurrentTimeOverride()

  const {
    loading,
    templates,
    visibleTemplates,
    activeCampaigns,
    primaryCampaign,
    isTemplateVisibleInCampaign,
    getTemplateCampaignTags,
  } = useTemplateLibraryData(currentTime)

  const value = useMemo<TemplateLibraryContextValue>(
    () => ({
      loading,
      currentTime,
      isPreviewSet,
      setPreviewDate,
      adjustPreviewByDays,
      activeCampaigns,
      primaryCampaign,
      templates,
      visibleTemplates,
      isTemplateVisibleInCampaign,
      getTemplateCampaignTags,
    }),
    [
      loading,
      currentTime,
      isPreviewSet,
      setPreviewDate,
      adjustPreviewByDays,
      activeCampaigns,
      primaryCampaign,
      templates,
      visibleTemplates,
      isTemplateVisibleInCampaign,
      getTemplateCampaignTags,
    ],
  )

  return (
    <TemplateLibraryContext.Provider value={value}>
      {children}
    </TemplateLibraryContext.Provider>
  )
}

/**
 * Hook to access the template library context.
 * Must be used within a TemplateLibraryProvider.
 */
export const useTemplateLibrary = (): TemplateLibraryContextValue => {
  const context = useContext(TemplateLibraryContext)
  if (!context) {
    throw new Error(
      'useTemplateLibrary must be used within a TemplateLibraryProvider',
    )
  }
  return context
}
